import type { ReactNode } from 'react';
import { cn } from '@herit-fe/heritage-ui';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { ElevatedCard, ELEVATED_SURFACE, ELEVATED_SURFACE_HOVER } from './elevated-card';

export interface StatSummaryCardProps {
  icon: LucideIcon;
  /**
   * 아이콘 **타일 배경 + 아이콘 색**을 함께 싣는다(예: `bg-amber-500/10 text-amber-600 dark:text-amber-400`).
   * 아이콘 자체는 크기만 갖고 색은 currentColor 로 상속 — 지표별 hue 를 호출부 한 곳에서 관리.
   */
  iconClassName: string;
  title: string;
  value: ReactNode;
  /** 값 위 소라벨(예: '오늘') — 상단 행 **우측**에 표기. 없으면 미렌더. */
  sublabel?: ReactNode;
  caption?: string;
  /** 값에 덧입힐 클래스(회색 dim·밑줄·강조 등). */
  valueClassName?: string;
  /** 카드 클릭(예: 미완료 수익 안내 팝업) — 없으면 정적 카드. */
  onClick?: () => void;
  className?: string;
}

/**
 * 지표 요약 카드 — `[아이콘 타일 · (서브라벨)] / 타이틀 / 큰 값 / (캡션)` 세로 스택. 대시보드 요약·운영 카드와
 * 수익/발전 리포트 상단 지표가 **동일 모양**을 쓰도록 shared 로 올린 SSOT(위젯끼리는 동일 레이어라 직접 공유 불가).
 *
 * 표면은 `ElevatedCard` — 콘텐츠(회색) 배경 위에 뜨는 카드 전제. `onClick` 을 주면 **카드 루트가 통째로
 * `<button>`** 이 된다(모서리까지 클릭 대상 + hover + 카드 모양에 맞는 포커스 링).
 */
export function StatSummaryCard({
  icon: Icon,
  iconClassName,
  title,
  value,
  sublabel,
  caption,
  valueClassName,
  onClick,
  className,
}: StatSummaryCardProps) {
  const body = (
    <>
      {/* 상단 행 — 좌: 아이콘 타일 / 우: 서브라벨 + (클릭 가능 시) chevron. `items-start` = 우측 묶음이 타일
          상단선에 맞춰 붙는다. chevron 이 여기 붙는 이유 — 카드가 세로 스택이라 trailing 자리가 이 행뿐이고,
          타이틀 옆에 두면 `truncate` 타이틀·큰 값과 폭을 다툰다. */}
      <div className="flex items-start justify-between gap-2">
        <span
          aria-hidden="true"
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-lg',
            iconClassName,
          )}
        >
          <Icon className="size-4.5" />
        </span>
        {(sublabel != null || onClick) && (
          <span className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground">
            {sublabel}
            {/* 클릭 가능 어포던스 — **정적** 신호가 필수다: 터치 환경(WebView 네이티브)에선 hover 가 아예 안
                보이고, 같은 모양 카드가 나란히 있는데 일부만 눌린다(예: 발전량=클릭 / 발전시간=정적).
                목록 타일(`plant-info-list`·`support-page`)과 **동일한 chevron** 으로 앱 전체 관용구를 맞춘다. */}
            {onClick && <ChevronRight className="size-4 shrink-0" aria-hidden="true" />}
          </span>
        )}
      </div>
      <span className="mt-3 truncate text-xs font-semibold text-foreground sm:text-sm">
        {title}
      </span>
      <span
        className={cn(
          'mt-1 text-sm font-bold leading-tight tabular-nums text-foreground sm:text-lg',
          valueClassName,
        )}
      >
        {value}
      </span>
      {caption != null && (
        <span className="mt-0.5 truncate text-xs text-muted-foreground">{caption}</span>
      )}
    </>
  );

  // 클릭 가능이면 **카드 루트 자체가 버튼**(네이티브 button — 키보드 포커스·엔터 기본 동작 확보).
  //
  // 카드 안에 버튼을 넣지 않는 이유 — 그러면 클릭 판정과 포커스 링이 카드 패딩 **안쪽 박스**에만 걸린다.
  // 카드 가장자리를 눌러도 반응이 없고(카드처럼 보이는데 일부만 눌리는 이질감), 포커스 링도 카드 모서리와
  // 어긋난 네모로 뜬다. 표면은 `ELEVATED_SURFACE + rounded-xl bg-card` 조합으로 `ElevatedCard` 와 동일하게
  // 맞춘다(발전소 목록 타일과 같은 관용구 — 그쪽도 Card 가 아니라 클릭 요소를 루트로 둔다).
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          ELEVATED_SURFACE,
          ELEVATED_SURFACE_HOVER,
          'flex flex-col items-stretch gap-0 rounded-xl bg-card p-3 text-left sm:p-4',
          'outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
      >
        {body}
      </button>
    );
  }

  return (
    <ElevatedCard className={cn('flex flex-col gap-0 p-3 sm:p-4', className)}>{body}</ElevatedCard>
  );
}
